/**
 * Shared order status cache so OrderList (order-details page) and TrackingBar
 * don't both call the order-detail API. One fetch updates the cache; the other
 * reads from it when fresh.
 */

const CACHE_PREFIX = 'order_detail_cache_';
const DEFAULT_MAX_AGE_MS = 20000; // 20 seconds - treat as fresh for this long

/**
 * Get numeric order id for cache key (handles "4242", "FYN00004242", etc.)
 * @param {string|number} orderId - Route id or formatted FYN id
 * @returns {string} Numeric string for cache key
 */
export function getOrderStatusCacheKey(orderId) {
  if (orderId == null) return '';
  const str = String(orderId).trim();
  if (str.startsWith('FYN')) {
    const numeric = str.replace(/^FYN0+/, '') || str.replace('FYN', '');
    return numeric || '';
  }
  return str;
}

/**
 * Write order status to shared cache (called after successful order-detail fetch).
 * @param {string|number} orderId - Order id (numeric or FYN format)
 * @param {string} status - Order status string
 */
export function setOrderStatusCache(orderId, status) {
  const key = getOrderStatusCacheKey(orderId);
  if (!key) return;
  try {
    sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({
      status,
      timestamp: Date.now(),
    }));
  } catch (_) {
    // ignore storage errors
  }
}

/**
 * Read from shared cache if present and not stale. Avoids duplicate API calls.
 * @param {string|number} orderId - Order id (numeric or FYN format)
 * @param {number} maxAgeMs - Max age in ms to consider cache valid (default 20s)
 * @returns {{ status: string } | null} Cached status or null if miss/stale
 */
export function getOrderStatusCache(orderId, maxAgeMs = DEFAULT_MAX_AGE_MS) {
  const key = getOrderStatusCacheKey(orderId);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const { status, timestamp } = JSON.parse(raw);
    if (!status || timestamp == null) return null;
    if (Date.now() - timestamp > maxAgeMs) return null;
    return { status };
  } catch (_) {
    return null;
  }
}
